/*
 * Shared cross-tool lens (motr-lens-v1).
 *
 * The lens is a small set of buyer-side answers that stitches Plan
 * Builder and the Due Diligence Workbench together. A buyer who fills
 * out the lens in one tool sees it pre-filled in the other.
 *
 * Schema is verbatim the prototype contract documented in
 * BUILD-INSTRUCTIONS §7. Reads are tolerant — backfill missing keys
 * against the canonical default. Writes always stamp source +
 * updatedAt so the analytics dashboard can attribute lens changes.
 *
 * Workbench-only fields (tax, changeInventoryTicked, redFlagAcknowledged)
 * are NOT part of the shared lens — those stay in the DDW state object.
 *
 * Stay or Go? does NOT participate in the shared lens — different
 * audience, different flow.
 */
const LENS_KEY = 'motr-lens-v1';

export type LensSource = 'plan_builder' | 'workbench';
export type LensFrictionPhase =
  | 'setup'
  | 'execution'
  | 'handoffs'
  | 'closure';
export type LensFlexibility = 'high' | 'medium' | 'low';

// Aliases — both naming conventions are exposed so neither tool's
// existing call sites need adapting. Pick one for new code.
export type FrictionPhase = LensFrictionPhase;
export type FlexibilityLevel = LensFlexibility;

export interface SharedLens {
  version: 1;
  updatedAt: string | null;
  source: LensSource | null;
  jobStatement: string;
  redFlagWordsHit: string[];
  frictionPhase: LensFrictionPhase | null;
  outcomes: [string, string, string];
  emotional: {
    feel: string[];
    avoid: string[];
  };
  flexibility: LensFlexibility | null;
}

export function createEmptyLens(): SharedLens {
  return {
    version: 1,
    updatedAt: null,
    source: null,
    jobStatement: '',
    redFlagWordsHit: [],
    frictionPhase: null,
    outcomes: ['', '', ''],
    emotional: { feel: [], avoid: [] },
    flexibility: null,
  };
}

/** Alias matching the DDW agent's API name. */
export function emptyLens(source: LensSource): SharedLens {
  return {
    ...createEmptyLens(),
    source,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Read the shared lens from localStorage. Returns null when no lens
 * has been written yet — the caller should fall back to its own
 * tool-local state in that case (and then mirror it back via writeLens
 * on the next save). Tolerant: malformed JSON or missing keys never
 * throw, they just produce an empty lens with whatever fields parsed.
 */
export function readLens(): SharedLens | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LENS_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<SharedLens> & {
      emotional?: Partial<SharedLens['emotional']>;
      outcomes?: unknown;
    };
    if (!parsed || typeof parsed !== 'object') return null;
    const lens = createEmptyLens();
    if (typeof parsed.jobStatement === 'string') {
      lens.jobStatement = parsed.jobStatement;
    }
    if (Array.isArray(parsed.redFlagWordsHit)) {
      lens.redFlagWordsHit = parsed.redFlagWordsHit.filter(
        (s): s is string => typeof s === 'string',
      );
    }
    if (
      parsed.frictionPhase === 'setup' ||
      parsed.frictionPhase === 'execution' ||
      parsed.frictionPhase === 'handoffs' ||
      parsed.frictionPhase === 'closure'
    ) {
      lens.frictionPhase = parsed.frictionPhase;
    }
    if (Array.isArray(parsed.outcomes)) {
      const padded = (parsed.outcomes as unknown[])
        .filter((s): s is string => typeof s === 'string')
        .concat(['', '', '']);
      lens.outcomes = [padded[0], padded[1], padded[2]];
    }
    if (parsed.emotional && typeof parsed.emotional === 'object') {
      const e = parsed.emotional;
      if (Array.isArray(e.feel)) {
        lens.emotional.feel = e.feel.filter(
          (s): s is string => typeof s === 'string',
        );
      }
      if (Array.isArray(e.avoid)) {
        lens.emotional.avoid = e.avoid.filter(
          (s): s is string => typeof s === 'string',
        );
      }
    }
    if (
      parsed.flexibility === 'high' ||
      parsed.flexibility === 'medium' ||
      parsed.flexibility === 'low'
    ) {
      lens.flexibility = parsed.flexibility;
    }
    if (
      parsed.source === 'plan_builder' ||
      parsed.source === 'workbench'
    ) {
      lens.source = parsed.source;
    }
    if (typeof parsed.updatedAt === 'string') {
      lens.updatedAt = parsed.updatedAt;
    }
    return lens;
  } catch {
    return null;
  }
}

/** Alias matching the DDW agent's API name. */
export const readSharedLens = readLens;

/**
 * Write the shared lens, stamping it with the calling tool's source
 * and the current ISO timestamp. Both Plan Builder and the Workbench
 * call this on every state save.
 */
export function writeLens(lens: SharedLens, source: LensSource): void {
  const stamped: SharedLens = {
    ...lens,
    source,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(LENS_KEY, JSON.stringify(stamped));
  } catch {
    /* private mode / disabled storage — local state still works,
       cross-tool stitching is just lost. */
  }
}

/**
 * DDW-style write: takes a lens body that already carries its own
 * source. Stamps version + updatedAt and persists. Provided so DDW's
 * call sites don't have to change.
 */
export function writeSharedLens(
  lens: Omit<SharedLens, 'version' | 'updatedAt'>,
): void {
  const payload: SharedLens = {
    ...lens,
    version: 1,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(LENS_KEY, JSON.stringify(payload));
  } catch {
    /* same fallback policy */
  }
}

/**
 * Clear the shared lens. Used by tool-local "reset" actions. Both
 * tools clear together so the buyer doesn't see stale lens content
 * after starting over.
 */
export function clearLens(): void {
  try {
    localStorage.removeItem(LENS_KEY);
  } catch {
    /* same fallback policy as readLens */
  }
}

/**
 * Hydration helper used by both tools on mount. Returns the lens to
 * use for first render.
 *
 * Order:
 *   1. If motr-lens-v1 exists, return it (it's the source of truth).
 *   2. Otherwise, if the tool has a non-empty fallback (e.g., a stale
 *      tool-local lens copy from a previous version of the app),
 *      return that and immediately persist it to motr-lens-v1.
 *   3. Otherwise return an empty lens.
 *
 * The `wasFromOtherTool` flag is used by the Plan Builder to render
 * the "we pulled in your lens from the Workbench" banner.
 */
export interface HydrationResult {
  lens: SharedLens;
  wasFromOtherTool: boolean;
}

export function hydrateLens(
  source: LensSource,
  fallback?: SharedLens,
): HydrationResult {
  const stored = readLens();
  if (stored) {
    const wasFromOtherTool =
      stored.source !== null &&
      stored.source !== source &&
      stored.jobStatement.trim().length > 0;
    return { lens: stored, wasFromOtherTool };
  }
  if (fallback && lensHasContent(fallback)) {
    writeLens(fallback, source);
    return { lens: fallback, wasFromOtherTool: false };
  }
  return { lens: createEmptyLens(), wasFromOtherTool: false };
}

/**
 * True if any user-supplied lens field is populated. Accepts either a
 * full SharedLens or a body without the wire-only fields (version,
 * updatedAt) so callers can ask the question without minting a full
 * envelope.
 */
export function lensHasContent(
  lens: Omit<SharedLens, 'version' | 'updatedAt'> | SharedLens,
): boolean {
  if (lens.jobStatement.trim()) return true;
  if (lens.frictionPhase) return true;
  if (lens.outcomes.some((o) => o && o.trim())) return true;
  if (lens.emotional.feel.length > 0) return true;
  if (lens.emotional.avoid.length > 0) return true;
  if (lens.flexibility) return true;
  return false;
}

// ─── Lens content (kept aligned with the Workbench) ──────────────────
// Exported so per-tool UIs can render pills / placeholders consistently.
// These mirror the prototype's hardcoded defaults; the admin doesn't
// yet expose them as configurable, so they live here for now.

export const LENS_FRICTION_PHASES: ReadonlyArray<{
  key: LensFrictionPhase;
  label: string;
  sub: string;
}> = [
  {
    key: 'setup',
    label: 'Setup',
    sub: 'Before work begins — how requests arrive, how you scope and assign',
  },
  {
    key: 'execution',
    label: 'Execution',
    sub: 'The core work — drafting, reviewing, collaborating, getting approvals',
  },
  {
    key: 'handoffs',
    label: 'Handoffs',
    sub: 'Between people or systems — transitions, version control, waiting',
  },
  {
    key: 'closure',
    label: 'Closure',
    sub: 'Completion — finalizing, storing, finding things later, reporting',
  },
];

export const LENS_FLEX_LEVELS: ReadonlyArray<{
  key: LensFlexibility;
  label: string;
  sub: string;
}> = [
  {
    key: 'high',
    label: 'High',
    sub: 'M&A, growth, or jurisdiction expansion likely in 24 months',
  },
  {
    key: 'medium',
    label: 'Medium',
    sub: 'Steady change — hiring, expansion, periodic reorgs',
  },
  {
    key: 'low',
    label: 'Low',
    sub: 'Stable team, narrow use case, mature industry',
  },
];

export const LENS_EMOTIONAL_FEEL: readonly string[] = [
  'Accessible',
  'Responsive',
  'Helpful',
  'Service-oriented',
  'Team players',
  'Professional',
  'Precise',
  'Collaborative',
  'Communicative',
  'Flexible/adaptable',
  'Praise-worthy',
];

export const LENS_EMOTIONAL_AVOID: readonly string[] = [
  'Being overworked',
  'Being burnt out',
  'Feeling disrespected by clients/mgmt',
  'Being unprepared for change',
  'Reputational harm (team or individual)',
  'Poor service delivery',
  'Avoidable mistakes',
  'Poor response times',
  'Poor quality work',
  'Needless administrivia / wasted steps',
];

export const LENS_OUTCOME_PLACEHOLDERS: readonly [string, string, string] = [
  'Reduce time from business request to first attorney response',
  'Reduce how often we discover critical deadlines too late to act',
  'Increase our ability to show leadership what the team actually delivers',
];

export const LENS_RED_FLAG_WORDS: readonly string[] = [
  'software',
  'platform',
  'system',
  'tool',
  'app',
  'application',
  'CLM',
  'CRM',
  'matter management',
  'e-billing',
  'ebilling',
  'AI',
  'A.I.',
  'artificial intelligence',
  'automation',
  'dashboard',
  'workflow',
  'workflows',
  'solution',
  'product',
  'vendor',
  'license',
  'SaaS',
];

/**
 * Find any solution-shaped words in a job statement. Returns the
 * matched canonical entries (preserving casing from the lexicon).
 * Whole-word matched, case-insensitive — so "automation" hits but
 * "automatically" does not.
 */
export function findRedFlagWords(jobStatement: string): string[] {
  const text = jobStatement.toLowerCase();
  if (!text.trim()) return [];
  return LENS_RED_FLAG_WORDS.filter((w) => {
    const escaped = w.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('\\b' + escaped + '\\b').test(text);
  });
}

/** Step-level validation used by the lens phase UI. */
export function isLensStepValid(lens: SharedLens, step: 1 | 2 | 3 | 4 | 5): boolean {
  switch (step) {
    case 1:
      return lens.jobStatement.trim().length >= 10;
    case 2:
      return lens.frictionPhase !== null;
    case 3:
      return lens.outcomes.filter((o) => o && o.trim().length > 0).length === 3;
    case 4:
      return lens.emotional.feel.length >= 1 && lens.emotional.avoid.length >= 1;
    case 5:
      return lens.flexibility !== null;
  }
}
